import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';

/* Only the columns the list query actually selects. Typing the mapper against
   the full Row breaks the moment a column is added to the table without being
   added to the select — which is exactly what happened when 0022 gave
   focus_tasks its governance links. */
type FocusTaskRow = Pick<
  Database['public']['Tables']['focus_tasks']['Row'],
  | 'id'
  | 'title'
  | 'description'
  | 'bucket'
  | 'priority'
  | 'due_at'
  | 'completed_at'
  | 'sort_order'
  | 'created_at'
  | 'updated_at'
>;
type FocusTaskInsert = Database['public']['Tables']['focus_tasks']['Insert'];
type FocusTaskUpdate = Database['public']['Tables']['focus_tasks']['Update'];

export type Bucket = 'today' | 'week' | 'backlog' | 'done';
export type Priority = 'low' | 'medium' | 'high' | 'urgent';

export const BUCKET_LABELS: Record<Bucket, string> = {
  today: 'היום',
  week: 'השבוע',
  backlog: 'בהמתנה',
  done: 'בוצע',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'נמוכה',
  medium: 'בינונית',
  high: 'גבוהה',
  urgent: 'דחופה',
};

export const PRIORITY_COLORS: Record<Priority, string> = {
  low: '#94a3b8',
  medium: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444',
};

export interface FocusTask {
  id: string;
  title: string;
  description: string | null;
  bucket: Bucket;
  priority: Priority;
  due_at: string | null;
  completed_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface FocusTaskInput {
  title: string;
  description: string | null;
  bucket: Bucket;
  priority: Priority;
  due_at: string | null;
}

export const focusTasksKey = (orgId: string | null) => ['focus-tasks', orgId] as const;

function castRow(r: FocusTaskRow): FocusTask {
  return {
    id: r.id,
    title: r.title,
    description: r.description,
    bucket: r.bucket as Bucket,
    priority: r.priority as Priority,
    due_at: r.due_at,
    completed_at: r.completed_at,
    sort_order: r.sort_order,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

async function fetchAll(orgId: string): Promise<FocusTask[]> {
  const { data, error } = await supabase
    .from('focus_tasks')
    .select('id, title, description, bucket, priority, due_at, completed_at, sort_order, created_at, updated_at, organization_id, created_by, deleted_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data.map(castRow);
}

export function useFocusTasks(orgId: string | null) {
  return useQuery({
    queryKey: focusTasksKey(orgId),
    queryFn: () => (orgId ? fetchAll(orgId) : Promise.resolve<FocusTask[]>([])),
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

export function useUpsertFocusTask(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: FocusTaskInput }) => {
      if (!orgId || !userId) throw new Error('Missing focus task context');
      const desc = values.description?.trim();
      const base = {
        title: values.title.trim(),
        description: desc && desc.length > 0 ? desc : null,
        bucket: values.bucket,
        priority: values.priority,
        due_at: values.due_at,
      };
      if (id) {
        const payload: FocusTaskUpdate = base;
        const { error } = await supabase.from('focus_tasks').update(payload).eq('id', id);
        if (error) throw error;
        return;
      }
      const payload: FocusTaskInsert = {
        ...base,
        organization_id: orgId,
        created_by: userId,
      };
      const { error } = await supabase.from('focus_tasks').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: focusTasksKey(orgId) });
    },
  });
}

export function useMoveFocusTask(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, bucket }: { id: string; bucket: Bucket }) => {
      const payload: FocusTaskUpdate = {
        bucket,
        completed_at: bucket === 'done' ? new Date().toISOString() : null,
      };
      const { error } = await supabase.from('focus_tasks').update(payload).eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: focusTasksKey(orgId) });
    },
  });
}

export function useDeleteFocusTask(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('focus_tasks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: focusTasksKey(orgId) });
    },
  });
}
